import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { legalUrls } from "@shopkit/shared";

import { useShopContext } from "../app/ShopGuard";
import { useCart } from "../features/cart/useCart";
import {
  defaultFulfillment,
  isCommentRequired,
  isCommentShown,
  isNameRequired,
} from "../features/checkout/checkoutFields";
import type { Fulfillment } from "../features/checkout/checkoutFields";
import { FulfillmentSection } from "../features/checkout/FulfillmentSection";
import { PaymentMethodSection } from "../features/checkout/PaymentMethodSection";
import { PhoneInput } from "../features/checkout/PhoneInput";
import { useCreateOrder } from "../features/checkout/useCreateOrder";
import { messages } from "../lib/messages";
import { maskPhone, toE164 } from "../lib/phone";
import { mainButton } from "../lib/telegram";

/**
 * CheckoutPage — оформление заказа (docs/07-miniapp-ux.md §2.5): способ
 * получения → адрес/пункт → контакты → оплата → комментарий. Живой пересчёт
 * «Итого» с доставкой на этот экран сознательно не добавлен: проект нигде
 * не считает и не форматирует деньги на фронте (см. lib/messages.ts,
 * minOrderHint), а бэкенд не отдаёт предпросмотр суммы с доставкой до
 * отправки заказа — настоящий total_text приходит только в ответе POST
 * (экран успеха). Товары и доставка показаны отдельными строками текстом.
 *
 * Обязательность полей — через нативную HTML-валидацию формы: она же по
 * стандарту браузера прокручивает к первому невалидному полю при попытке
 * отправки (docs/07-miniapp-ux.md §2.5: «при отправке — прокрутка
 * к первой ошибке»), поэтому JS-валидация полей не дублируется.
 */
export function CheckoutPage() {
  const { shop, customerFirstName } = useShopContext();
  const navigate = useNavigate();
  const cart = useCart();
  const createOrder = useCreateOrder();
  const formRef = useRef<HTMLFormElement>(null);

  const [fulfillment, setFulfillment] = useState<Fulfillment>(() => defaultFulfillment(shop));
  const [address, setAddress] = useState("");
  const [pickupPointId, setPickupPointId] = useState("");
  const [contactName, setContactName] = useState(customerFirstName);
  const [contactPhone, setContactPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(shop.payment_methods[0]?.code ?? "");
  const [comment, setComment] = useState("");
  // Галочка пустая по умолчанию: предварительно проставленное согласие
  // по 152-ФЗ согласием не считается — оно должно быть активным действием.
  const [consentAccepted, setConsentAccepted] = useState(false);

  const hasItems = Boolean(cart.data && cart.data.items.length > 0);

  useEffect(() => {
    if (!cart.isPending && !hasItems) {
      navigate("../cart", { replace: true });
    }
  }, [cart.isPending, hasItems, navigate]);

  useEffect(() => {
    if (!createOrder.data) {
      return;
    }
    navigate("success", {
      replace: true,
      state: {
        order: createOrder.data,
        fulfillment,
        maskedPhone: maskPhone(toE164(contactPhone)),
      },
    });
  }, [createOrder.data, navigate, fulfillment, contactPhone]);

  useEffect(() => {
    mainButton.setText(messages.submitOrder);
    mainButton.show();
    return mainButton.onClick(() => formRef.current?.requestSubmit());
  }, []);

  useEffect(() => {
    return () => mainButton.hide();
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (createOrder.isPending) {
      return;
    }

    createOrder.submit({
      fulfillment,
      pickup_point_id: fulfillment === "pickup" ? pickupPointId : undefined,
      address: fulfillment === "delivery" ? address : undefined,
      payment_method: paymentMethod,
      contact_name: contactName,
      contact_phone: toE164(contactPhone),
      comment: isCommentShown(shop) ? comment : undefined,
      consent_accepted: consentAccepted,
    });
  }

  if (cart.isPending) {
    return <CheckoutSkeleton />;
  }

  if (!hasItems) {
    return null;
  }

  const fieldError = (name: string) => createOrder.error.fields[name];

  return (
    <div className="min-h-screen bg-bg pb-24">
      <header className="p-4">
        <h1 className="text-lg font-semibold text-text">{messages.checkout}</h1>
      </header>

      <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-4 px-4">
        <FulfillmentSection
          shop={shop}
          fulfillment={fulfillment}
          onChangeFulfillment={setFulfillment}
          address={address}
          onChangeAddress={setAddress}
          pickupPointId={pickupPointId}
          onChangePickupPointId={setPickupPointId}
          addressError={fieldError("address")}
          pickupPointError={fieldError("pickup_point_id")}
        />

        <div className="flex flex-col gap-1">
          <label htmlFor="checkout-name" className="text-sm text-hint">
            {messages.nameLabel}
          </label>
          <input
            id="checkout-name"
            name="contact_name"
            required={isNameRequired(shop)}
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
            className="rounded-card bg-surface px-4 py-2 text-text"
          />
          {fieldError("contact_name") ? <p className="text-sm text-red-500">{fieldError("contact_name")}</p> : null}
        </div>

        <PhoneInput value={contactPhone} onChange={setContactPhone} error={fieldError("contact_phone")} />

        <PaymentMethodSection shop={shop} value={paymentMethod} onChange={setPaymentMethod} />
        {fieldError("payment_method") ? <p className="text-sm text-red-500">{fieldError("payment_method")}</p> : null}

        {isCommentShown(shop) ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="checkout-comment" className="text-sm text-hint">
              {messages.commentLabel}
            </label>
            <textarea
              id="checkout-comment"
              name="comment"
              required={isCommentRequired(shop)}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={messages.commentPlaceholder}
              className="rounded-card bg-surface px-4 py-2 text-text placeholder:text-hint"
              rows={3}
            />
            {fieldError("comment") ? <p className="text-sm text-red-500">{fieldError("comment")}</p> : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-1 text-sm">
          <div className="flex justify-between text-hint">
            <span>{messages.itemsTotal}</span>
            <span>{cart.data?.items_total_text}</span>
          </div>
          <div className="flex justify-between text-hint">
            <span>{messages.delivery}</span>
            <span>{fulfillment === "pickup" ? messages.deliveryFree : shop.delivery.price_text}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-text">
            <input
              type="checkbox"
              name="consent_accepted"
              required
              checked={consentAccepted}
              onChange={(event) => setConsentAccepted(event.target.checked)}
              className="mt-0.5 h-4 w-4 flex-none"
            />
            <span>
              {messages.consentLabel}{" "}
              <a href={legalUrls.privacy} target="_blank" rel="noreferrer" className="underline">
                {messages.consentPolicyLink}
              </a>
            </span>
          </label>
          {fieldError("consent_accepted") ? (
            <p className="text-sm text-red-500">{fieldError("consent_accepted")}</p>
          ) : null}
        </div>

        {createOrder.error.message ? <p className="text-sm text-red-500">{createOrder.error.message}</p> : null}
      </form>
    </div>
  );
}

function CheckoutSkeleton() {
  return (
    <div className="flex min-h-screen flex-col gap-3 bg-bg p-4">
      <div className="h-10 animate-pulse rounded-card bg-surface" />
      <div className="h-10 animate-pulse rounded-card bg-surface" />
      <div className="h-10 animate-pulse rounded-card bg-surface" />
    </div>
  );
}
